import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  PageHeader,
  Card,
  CardBody,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  Pagination,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell
} from '../components/common';
import {
  getBuildingModuleName,
  getRegisterTypeName,
  getRegisterTypeVariant
} from '../constants/buildings';

const UserBuildings = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchUserDetails();
    fetchUserBuildings();
  }, [userId, currentPage]);

  const fetchUserDetails = async () => {
    try {
      const response = await api.get(`/Users/${userId}`);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const fetchUserBuildings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/Users/${userId}/buildings`, {
        params: {
          pageNumber: currentPage,
          pageSize: pageSize
        }
      });
      setBuildings(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch user buildings');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Buildings for ${user?.firstName} ${user?.lastName}`}
        subtitle={user?.email}
        backLink={{ label: "Back to User Details", href: `/users/${userId}` }}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <Card>
        <CardBody>
          {buildings.length === 0 ? (
            <EmptyState
              message="This user is not a member of any buildings"
              icon="🏢"
            />
          ) : (
            <ListContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>Building</TableCell>
                    <TableCell header>Registration Type</TableCell>
                    <TableCell header>Modules</TableCell>
                    <TableCell header>
                      <span className="sr-only">Actions</span>
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {buildings.map((building) => (
                    <TableRow key={building.id} clickable onClick={() => navigate(`/buildings/${building.id}`)}>
                      <TableCell>
                        <div className="font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                          {building.name}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          Username: {building.userName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRegisterTypeVariant(building.registerType)}>
                          {getRegisterTypeName(building.registerType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          {building.selectedModules && building.selectedModules.length > 0 ? (
                            building.selectedModules.map((module, index) => (
                              <Badge key={index} variant="primary" size="small">
                                {getBuildingModuleName(module)}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-slate-400 italic">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end pr-4">
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/buildings/${building.id}`);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ListContainer>
          )}
        </CardBody>
      </Card>

      {buildings.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalCount={totalCount}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

export default UserBuildings;